import { Grid, Typography } from "@material-ui/core";
import {
  annualIncomeItems,
  natureOfBusinessItems,
  occupationItems,
  sourceOfFundsItems,
} from "../options";
import SectionCard from "../ui/SectionCard";
import { BriefcaseIcon, FileTextIcon } from "../ui/icons";
import { hasValue as hasIdValue } from "../logic/idValidation";
import LockedField from "../ui/fields/LockedField";
import SelectField from "../ui/fields/SelectField";
import TextField from "../ui/fields/TextField";
import { occupationSlots, setOccupationSlot } from "../logic/occupation";
import IdDocuments from "./IdDocuments";
import { StepViewProps } from "./types";

export default function Professional({
  state,
  isTier3,
  handlers,
}: StepViewProps) {
  const { draft, errors } = state;
  const kyc = draft.kyc;
  const err = (path: string) => errors[path];
  const ids = kyc.ids || {};
  const originalIds = state.originalKyc.ids || {};
  const hasTinOnFile = hasIdValue(originalIds.TIN);
  const hasSssOnFile = hasIdValue(originalIds.SSS);
  const hasTinSssOnFile = hasTinOnFile || hasSssOnFile;
  const hasTin = hasTinOnFile || hasIdValue(ids.TIN);
  const setTaxId = (field: "TIN" | "SSS", value: string) => {
    handlers.setKycField(`ids.${field}`, value);
  };

  if (isTier3) {
    const [occupation, position] = occupationSlots(kyc.work?.primaryOccupation);
    return (
      <SectionCard
        title="Professional details"
        icon={<BriefcaseIcon size={18} />}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SelectField
              label="Occupation"
              required
              value={occupation}
              items={occupationItems}
              error={err("work.primaryOccupation")}
              onChange={(v) =>
                handlers.setKycField(
                  "work.primaryOccupation",
                  setOccupationSlot(kyc.work?.primaryOccupation, 0, String(v))
                )
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Position"
              required
              value={position}
              error={err("work.primaryOccupation")}
              onChange={(v) =>
                handlers.setKycField(
                  "work.primaryOccupation",
                  setOccupationSlot(kyc.work?.primaryOccupation, 1, v)
                )
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name of company or employer"
              required
              value={kyc.work?.nameOfEmployer}
              error={err("work.nameOfEmployer")}
              onChange={(v) => handlers.setKycField("work.nameOfEmployer", v)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              label="Nature of business"
              required
              value={kyc.work?.businessNature}
              items={natureOfBusinessItems}
              error={err("work.businessNature")}
              onChange={(v) => handlers.setKycField("work.businessNature", v)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              label="Annual income"
              required
              value={kyc.work?.annualIncomeAmount}
              items={annualIncomeItems}
              error={err("work.annualIncomeAmount")}
              onChange={(v) =>
                handlers.setKycField("work.annualIncomeAmount", v)
              }
            />
          </Grid>
        </Grid>
      </SectionCard>
    );
  }

  // Tier 2: occupation + source of funds + the government ID group/uploads.
  return (
    <>
      <SectionCard
        title="Work & source of funds"
        icon={<BriefcaseIcon size={18} />}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SelectField
              label="Primary occupation"
              required
              value={occupationSlots(kyc.work?.primaryOccupation)[0]}
              items={occupationItems}
              error={err("work.primaryOccupation")}
              onChange={(v) =>
                handlers.setKycField("work.primaryOccupation", v)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              label="Source of funds"
              required
              value={kyc.work?.sourceOfFunds}
              items={sourceOfFundsItems}
              error={err("work.sourceOfFunds")}
              onChange={(v) => handlers.setKycField("work.sourceOfFunds", v)}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        title="Government ID"
        icon={<FileTextIcon size={18} />}
        verified={hasTinSssOnFile}
        helperNote={
          hasTinSssOnFile
            ? "Identification numbers already on file can only be viewed."
            : undefined
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {hasTinOnFile ? (
              <LockedField label="TIN" value={ids.TIN || originalIds.TIN} />
            ) : (
              <TextField
                label="TIN"
                value={ids.TIN}
                onChange={(v) => setTaxId("TIN", v)}
              />
            )}
            {!hasTin && (
              <Typography
                component="p"
                variant="caption"
                color="textSecondary"
                style={{ margin: "6px 0 0" }}
              >
                Note: TIN number is required by certain partners to invest in
                select funds.
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {hasSssOnFile ? (
              <LockedField
                label="SSS / GSIS"
                value={ids.SSS || originalIds.SSS}
              />
            ) : (
              <TextField
                label="SSS / GSIS"
                value={ids.SSS}
                onChange={(v) => setTaxId("SSS", v)}
              />
            )}
          </Grid>
        </Grid>
        <IdDocuments state={state} handlers={handlers} />
      </SectionCard>
    </>
  );
}
